import { Button, Drawer, Segmented, Skeleton, Spin, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef, useState, type UIEvent } from 'react'
import { MdClose, MdDoneAll, MdOutlineNotificationsNone } from 'react-icons/md'

import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { cx } from '@/Ship/Helpers/cssClass'
import { pluralizeUk } from '@/Ship/Helpers/pluralize'

import { getDayLabel } from '../Helpers/getDayLabel'
import { notificationManager, type Notification } from '../Stores/NotificationManager'
import { ChangeAllNotificationsIsReadedTask } from '../Tasks/ChangeAllNotificationsIsReadedTask'
import { ChangeNotificationIsReadedTask } from '../Tasks/ChangeNotificationIsReadedTask'
import { LoadNotificationListTask } from '../Tasks/LoadNotificationListTask'

import styles from './NotificationDrawer.module.css'
import { NotificationListItem } from './NotificationListItem'

import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'

interface NotificationDrawerProps {
    placement?: 'bottom' | 'right'
}

type NotificationTab = 'all' | 'unread'

// Сповіщення диспетчера посилаються на сутність через (entityCode, entityUuid).
// Бекенд проставляє 'Assignment' (uuid завдання) або 'TransportRequest'
// (uuid заявки) — див. *Action::setEntityCode на бекенді.
//
// Ресурси вантажимо ЛІНИВО через dynamic import: статичний імпорт
// RequestResource/AssignmentResource сюди створює циклічну залежність
// (Resource → Template → … → drawer), через що на старті падало
// "Cannot access 'X' before initialization" (TDZ). Ледачий import()
// розриває цикл — модуль ресурсу обчислюється лише в момент кліку.
const ENTITY_RESOURCE_LOADER_BY_CODE: Record<string, () => Promise<IResourceConfig>> = {
    Assignment: () =>
        import('@/Containers/AssignmentSection/AssignmentContainer/Resources/AssignmentResource').then(
            (m) => m.AssignmentResource
        ),
    TransportRequest: () =>
        import('@/Containers/RequestSection/RequestContainer/Resources/RequestResource').then((m) => m.RequestResource),
}

/** За скільки пікселів до низу списку довантажувати наступну сторінку */
const LOAD_MORE_THRESHOLD = 120

/** Розмір сторінки списку: перша сторінка малюється швидко, решта довантажується при прокрутці */
const PAGE_SIZE = 20

type ListEntry = { kind: 'divider'; key: string; label: string } | { kind: 'item'; key: string; item: Notification }

/**
 * Шухляда сповіщень: перемикач «Всі / Непрочитані», список із розділювачами по днях,
 * автодовантаження при прокрутці, «Прочитати всі» у шапці, позначення прочитаним
 * з рядка. На десктопі відкривається праворуч, на телефоні знизу.
 */
export const NotificationDrawer = observer(({ placement = 'bottom' }: NotificationDrawerProps) => {
    const {
        isDrawerOpen,
        notifications,
        isLoadingFirstPage,
        isNotificationLoading,
        entityFilter,
        unreadCount,
        hasMore,
        page,
    } = notificationManager

    const [tab, setTab] = useState<NotificationTab>('all')
    const listRef = useRef<HTMLDivElement | null>(null)

    const tabParams = useCallback((current: NotificationTab) => (current === 'unread' ? { isReaded: false } : {}), [])

    const loadFirstPage = useCallback(
        (current: NotificationTab) => {
            notificationManager.setPagination({ page: 1, total: 0 })
            notificationManager.setNotifications([])
            void LoadNotificationListTask({ searchParams: { page: 1, limit: PAGE_SIZE, ...tabParams(current) } })
            listRef.current?.scrollTo({ top: 0 })
        },
        [tabParams]
    )

    useEffect(() => {
        if (isDrawerOpen) loadFirstPage(tab)
        // entityFilter читається всередині LoadNotificationListTask; перезавантажуємо,
        // коли змінюється область (наприклад, шухляда відкрита для одного завдання)
    }, [isDrawerOpen, tab, entityFilter, loadFirstPage])

    const handleScroll = (event: UIEvent<HTMLDivElement>) => {
        const el = event.currentTarget
        if (!hasMore || isNotificationLoading) return
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - LOAD_MORE_THRESHOLD) {
            void LoadNotificationListTask({ searchParams: { page: page + 1, limit: PAGE_SIZE, ...tabParams(tab) } })
        }
    }

    const entries = useMemo<ListEntry[]>(() => {
        const result: ListEntry[] = []
        let lastLabel: string | null = null
        notifications.forEach((item) => {
            const label = getDayLabel(item.createdAt)
            if (label !== lastLabel) {
                result.push({ kind: 'divider', key: `divider-${label}-${item.id}`, label })
                lastLabel = label
            }
            result.push({ kind: 'item', key: item.uuid, item })
        })
        return result
    }, [notifications])

    const openItem = async (item: Notification) => {
        if (!item.isReaded) void ChangeNotificationIsReadedTask({ uuid: item.uuid })

        // Відкриваємо картку завдання/заявки, до якої належить сповіщення. Список
        // закриваємо, щоб картка не опинилась під ним за z-index.
        const loadResource = item.entityCode ? ENTITY_RESOURCE_LOADER_BY_CODE[item.entityCode] : undefined
        if (loadResource && item.entityUuid) {
            notificationManager.setIsDrawerOpen(false)
            const resource = await loadResource()
            OpenResourceCardTask({ resource, primaryFieldValue: item.entityUuid, mode: 'view' })
        }
    }

    const markRead = (item: Notification) => {
        void ChangeNotificationIsReadedTask({ uuid: item.uuid })
    }

    // «Прочитати всі» б'є у глобальний ендпоінт, тому прихована, коли шухляда
    // обмежена одним завданням: інакше позначила б прочитаними й чужі сповіщення
    const canMarkAll = !entityFilter && unreadCount > 0

    const markAll = async () => {
        await ChangeAllNotificationsIsReadedTask()
        // Перезавантажуємо з фільтром вкладки, щоб «Непрочитані» очистились
        loadFirstPage(tab)
    }

    const unreadLabel =
        unreadCount > 0
            ? `${unreadCount} ${pluralizeUk(unreadCount, ['непрочитане', 'непрочитані', 'непрочитаних'])}`
            : 'Непрочитаних немає'

    const title = (
        <div className={cx(styles, 'title')}>
            <div className={cx(styles, 'titleMain')}>{entityFilter ? 'Сповіщення завдання' : 'Сповіщення'}</div>
            {!entityFilter && <div className={cx(styles, 'titleSub')}>{unreadLabel}</div>}
        </div>
    )

    const isEmpty = !isLoadingFirstPage && notifications.length === 0

    return (
        <Drawer
            placement={placement}
            size={placement === 'bottom' ? '82vh' : 440}
            title={title}
            open={isDrawerOpen}
            closeIcon={<MdClose />}
            onClose={() => notificationManager.setIsDrawerOpen(false)}
            rootClassName={cx(styles, 'drawer')}
            classNames={{
                header: cx(styles, 'header'),
                body: cx(styles, 'body'),
                wrapper: placement === 'bottom' ? cx(styles, 'wrapperBottom') : '',
            }}
            extra={
                canMarkAll ? (
                    <Tooltip title="Позначити всі сповіщення прочитаними">
                        <Button
                            size="small"
                            type="text"
                            className={cx(styles, 'markAll')}
                            icon={<MdDoneAll />}
                            onClick={markAll}
                            disabled={isNotificationLoading}
                        >
                            Прочитати всі
                        </Button>
                    </Tooltip>
                ) : undefined
            }
        >
            <div className={cx(styles, 'tabs')}>
                <Segmented
                    block
                    value={tab}
                    onChange={(value) => setTab(value as NotificationTab)}
                    options={[
                        { value: 'all', label: 'Всі' },
                        {
                            value: 'unread',
                            label: (
                                <span className={cx(styles, 'tab')}>
                                    Непрочитані
                                    {!entityFilter && unreadCount > 0 && (
                                        <span className={cx(styles, 'tabCount')}>
                                            {unreadCount > 99 ? '99+' : unreadCount}
                                        </span>
                                    )}
                                </span>
                            ),
                        },
                    ]}
                />
            </div>

            <div ref={listRef} className={cx(styles, 'list')} onScroll={handleScroll}>
                {isLoadingFirstPage && (
                    <div className={cx(styles, 'skeleton')} aria-busy="true">
                        {[0, 1, 2, 3].map((index) => (
                            <Skeleton
                                key={index}
                                active
                                avatar={{ shape: 'square', size: 40 }}
                                paragraph={{ rows: 1 }}
                            />
                        ))}
                    </div>
                )}

                {isEmpty && (
                    <div className={cx(styles, 'empty')}>
                        <div className={cx(styles, 'emptyIcon')}>
                            {tab === 'unread' ? <MdDoneAll /> : <MdOutlineNotificationsNone />}
                        </div>
                        <div className={cx(styles, 'emptyTitle')}>
                            {tab === 'unread' ? 'Усе прочитано' : 'Поки що тихо'}
                        </div>
                        <div className={cx(styles, 'emptyText')}>
                            {tab === 'unread' ? 'Непрочитаних сповіщень немає' : 'Нові сповіщення з’являться тут'}
                        </div>
                    </div>
                )}

                {!isLoadingFirstPage &&
                    entries.map((entry) =>
                        entry.kind === 'divider' ? (
                            <div key={entry.key} className={cx(styles, 'divider')}>
                                {entry.label}
                            </div>
                        ) : (
                            <NotificationListItem
                                key={entry.key}
                                item={entry.item}
                                onClick={openItem}
                                onMarkRead={markRead}
                            />
                        )
                    )}

                {!isLoadingFirstPage && isNotificationLoading && (
                    <div className={cx(styles, 'loadingMore')}>
                        <Spin size="small" /> Завантаження…
                    </div>
                )}
            </div>
        </Drawer>
    )
})
